/**
 * Location Filter Dialogue
 * Table-based location picker matching FilterDialogs.jsx style
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  InputAdornment, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, Typography, Box, useTheme, alpha
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';

export default function LocationFilterDialogue({
  open,
  onClose,
  locations,
  selectedLocation,
  onApply
}) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogSelected, setDialogSelected] = useState(selectedLocation || '');

  // Reset dialog state when opening
  React.useEffect(() => {
    if (open) {
      setDialogSelected(selectedLocation || '');
      setSearchTerm('');
    }
  }, [open, selectedLocation]);

  const filteredLocations = (locations || []).filter(l =>
    l.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleApply = () => {
    onApply(dialogSelected || '');
  };

  const rowSx = {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'grey.50',
      transform: 'translateX(4px)',
      boxShadow: 'inset 4px 0 0 black'
    },
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
      py: 2,
      pl: 3,
      pr: 3
    }
  };

  const selectBtnSx = (selected) => ({
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8rem',
    px: 2,
    py: 1,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    bgcolor: selected ? 'black' : 'white',
    color: selected ? 'white' : 'text.primary',
    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
      bgcolor: selected ? 'black' : 'white',
      color: selected ? 'white' : 'text.primary'
    }
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: '80vh',
          maxHeight: '80vh',
          margin: 'auto',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
          Filter by Location
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <TextField
            fullWidth
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              }
            }}
          />
        </Box>

        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* All Locations row */}
              <TableRow hover sx={rowSx}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      All Locations
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show transactions for all locations
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDialogSelected('')}
                    sx={selectBtnSx(dialogSelected === '')}
                  >
                    Select
                  </Button>
                </TableCell>
              </TableRow>

              {filteredLocations.map(loc => (
                <TableRow key={loc.id} hover sx={rowSx}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {loc.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDialogSelected(loc.id)}
                      sx={selectBtnSx(dialogSelected === loc.id)}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5)
      }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, px: 3 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            borderRadius: 2,
            px: 3,
            bgcolor: 'black',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
